import { useEffect, useState } from "react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";

import "@/compat/historyState";
import { clearEntryIntentFromAddress } from "@/engine/state/entryIntent";
import { RoomRoute } from "@/components/room/RoomRoute";
import { noteRoomInvitation } from "@/hooks/usePairOffers";

/**
 * A room, at an address of its own.
 *
 * The code is a path segment rather than a query argument (#610), so this is
 * what the application publishes as an invitation and what a visitor can read
 * before they press anything. What the press decided — that this is an opening,
 * whether the row was written, the preferences it opens on — travels in the
 * history entry.
 *
 * **It is spent on arrival.** A history entry outlives the page that made it:
 * the browser keeps `history.state` across a reload, so a room opened once and
 * reloaded came back still calling itself an opening — and an opening reads no
 * row, so the room forgot the planes its host had set (#646). It is taken once
 * and rubbed out, which makes a reload what it actually is: an ordinary join,
 * into a room that already exists and knows what it is.
 */
export const Route = createFileRoute("/room/$code")({
  component: RoomCodeRoute,
});

function RoomCodeRoute() {
  const { code } = Route.useParams();
  const navigate = Route.useNavigate();
  const state = useRouterState({ select: (s) => s.location.state });
  // Read on the first render, before the rubbing out below can take it away.
  const [press] = useState(() => ({
    opening: state.opening,
    recordRefused: state.recordRefused,
    preferences: state.preferences,
    roomTitle: state.roomTitle,
    handedOver: state.handedOver,
  }));

  useEffect(() => {
    // A sign-in round trip comes home to `/` and is sent on with its query;
    // what this application put in that query is spent by the time it lands.
    clearEntryIntentFromAddress();
    if (!press.opening && !press.handedOver) return;
    void navigate({
      to: ".",
      replace: true,
      state: (prev) => ({
        ...prev,
        opening: undefined,
        recordRefused: undefined,
        preferences: undefined,
        roomTitle: undefined,
        handedOver: undefined,
      }),
    });
  }, [navigate, press.opening, press.handedOver]);

  // An invitation opened on a phone may be carried to a computer from the
  // threshold, before anybody has walked in and made it a visit.
  useEffect(() => noteRoomInvitation(code), [code]);

  return (
    <RoomRoute
      code={code.toLowerCase()}
      opening={press.opening}
      recordRefused={press.recordRefused}
      preferences={press.preferences}
      roomTitle={press.roomTitle}
      handedOver={press.handedOver}
    />
  );
}
